<template>
  <div class="langganan-container">
    <div class="header">
      <h1>Langganan HobiBaca</h1>
      <p>Akses penuh ke semua buku digital dengan satu langganan sederhana</p>
    </div>

    <div class="single-subscription">
      <LanggananBox
        title="Semua Buku"
        price="Rp 50.000"
        description="Akses lengkap ke semua koleksi buku HobiBaca."
        :features="features"
        link="/langganan"
        :isPopular="true"
        :isPremium="true"
        :planId="1"
        @subscription-error="handleSubscriptionError"
        @button-click="showPaymentModal = true"
      />
    </div>

    <!-- Auth Modal -->
    <div class="modal-overlay" v-if="showLoginModal" @click.self="closeLoginModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isLoginMode ? 'Login untuk Berlangganan' : 'Daftar untuk Berlangganan' }}</h2>
          <button class="close-button" @click="closeLoginModal">&times;</button>
        </div>
        <div class="modal-body">
          <LoginForm 
            v-if="isLoginMode"
            :redirectHome="false"
            @login-success="handleLoginSuccess"
          />
          <RegisterForm
            v-else
            :redirectAfterRegister="null"
            @register-success="handleRegisterSuccess"
          />
          <div class="auth-divider">
            <span>atau</span>
          </div>
          <p class="auth-switch">
            {{ isLoginMode ? 'Belum punya akun?' : 'Sudah punya akun?' }}
            <a href="#" @click.prevent="toggleAuthMode">
              {{ isLoginMode ? 'Daftar sekarang' : 'Login di sini' }}
            </a>
          </p>
        </div>
      </div>
    </div>

    <!-- Payment Method Modal -->
    <div class="modal-overlay" v-if="showPaymentModal && isLoggedIn" @click.self="closePaymentModal">
      <div class="modal-content payment-modal">
        <div class="modal-header">
          <h3>Pilih Metode Pembayaran</h3>
          <button class="close-button" @click="closePaymentModal">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="paymentErrorMessage" class="payment-error-message">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="error-icon" />
            {{ paymentErrorMessage }}
          </div>
          <div class="payment-methods">
            <div 
              class="payment-method-card" 
              :class="{ 'selected': paymentMethod === 'bank_transfer' }"
              @click="selectPaymentMethod('bank_transfer')"
            >
              <img src="@/assets/bank-transfer.png" alt="Bank Transfer" class="payment-icon">
              <div class="method-details">
                <span class="method-name">Transfer Bank</span>
                <span class="method-desc">BCA, BNI, Mandiri, Permata</span>
              </div>
              <div class="check-indicator" v-if="paymentMethod === 'bank_transfer'">
                <font-awesome-icon :icon="['fas', 'check-circle']" />
              </div>
            </div>
            
            <div 
              class="payment-method-card" 
              :class="{ 'selected': paymentMethod === 'credit_card' }"
              @click="selectPaymentMethod('credit_card')"
            >
              <img src="@/assets/credit-card.png" alt="Credit Card" class="payment-icon">
              <div class="method-details">
                <span class="method-name">Kartu Kredit</span>
                <span class="method-desc">Visa, Mastercard, JCB</span>
              </div>
              <div class="check-indicator" v-if="paymentMethod === 'credit_card'">
                <font-awesome-icon :icon="['fas', 'check-circle']" />
              </div>
            </div>
            
            <div 
              class="payment-method-card" 
              :class="{ 'selected': paymentMethod === 'e_wallet' }"
              @click="selectPaymentMethod('e_wallet')"
            >
              <img src="@/assets/e-wallet.png" alt="E-Wallet" class="payment-icon">
              <div class="method-details">
                <span class="method-name">E-Wallet</span>
                <span class="method-desc">GoPay, OVO, Dana, ShopeePay</span>
              </div>
              <div class="check-indicator" v-if="paymentMethod === 'e_wallet'">
                <font-awesome-icon :icon="['fas', 'check-circle']" />
              </div>
            </div>
          </div>
          
          <!-- Bank Selection (only shown when bank_transfer is selected) -->
          <div class="bank-selection" v-if="paymentMethod === 'bank_transfer'">
            <h4>Pilih Bank</h4>
            <div class="bank-options">
              <div 
                class="bank-option" 
                :class="{ 'selected': selectedBank === 'bca' }"
                @click="selectedBank = 'bca'"
              >
                <img src="@/assets/bank-bca.png" alt="BCA" class="bank-logo">
                <span>BCA</span>
                <div class="check-indicator" v-if="selectedBank === 'bca'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="bank-option" 
                :class="{ 'selected': selectedBank === 'bni' }"
                @click="selectedBank = 'bni'"
              >
                <img src="@/assets/bank-bni.png" alt="BNI" class="bank-logo">
                <span>BNI</span>
                <div class="check-indicator" v-if="selectedBank === 'bni'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="bank-option" 
                :class="{ 'selected': selectedBank === 'mandiri' }"
                @click="selectedBank = 'mandiri'"
              >
                <img src="@/assets/bank-mandiri.png" alt="Mandiri" class="bank-logo">
                <span>Mandiri</span>
                <div class="check-indicator" v-if="selectedBank === 'mandiri'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="bank-option" 
                :class="{ 'selected': selectedBank === 'permata' }"
                @click="selectedBank = 'permata'"
              >
                <img src="@/assets/bank-permata.png" alt="Permata" class="bank-logo">
                <span>Permata</span>
                <div class="check-indicator" v-if="selectedBank === 'permata'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
            </div>
          </div>
          
          <!-- E-Wallet Selection (only shown when e_wallet is selected) -->
          <div class="wallet-selection" v-if="paymentMethod === 'e_wallet'">
            <h4>Pilih E-Wallet</h4>
            <div class="wallet-options">
              <div 
                class="wallet-option" 
                :class="{ 'selected': selectedWallet === 'gopay' }"
                @click="selectedWallet = 'gopay'"
              >
                <img src="@/assets/wallet-gopay.png" alt="GoPay" class="wallet-logo">
                <span>GoPay</span>
                <div class="check-indicator" v-if="selectedWallet === 'gopay'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="wallet-option" 
                :class="{ 'selected': selectedWallet === 'ovo' }"
                @click="selectedWallet = 'ovo'"
              >
                <img src="@/assets/wallet-ovo.png" alt="OVO" class="wallet-logo">
                <span>OVO</span>
                <div class="check-indicator" v-if="selectedWallet === 'ovo'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="wallet-option" 
                :class="{ 'selected': selectedWallet === 'dana' }"
                @click="selectedWallet = 'dana'"
              >
                <img src="@/assets/wallet-dana.png" alt="DANA" class="wallet-logo">
                <span>DANA</span>
                <div class="check-indicator" v-if="selectedWallet === 'dana'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
              
              <div 
                class="wallet-option" 
                :class="{ 'selected': selectedWallet === 'shopeepay' }"
                @click="selectedWallet = 'shopeepay'"
              >
                <img src="@/assets/wallet-shopeepay.png" alt="ShopeePay" class="wallet-logo">
                <span>ShopeePay</span>
                <div class="check-indicator" v-if="selectedWallet === 'shopeepay'">
                  <font-awesome-icon :icon="['fas', 'check-circle']" />
                </div>
              </div>
            </div>
          </div>
          
          <button 
            class="proceed-payment-btn" 
            @click="processMidtransPayment"
            :disabled="!isPaymentMethodSelected || isProcessingPayment"
          >
            <span v-if="isProcessingPayment">
              <font-awesome-icon :icon="['fas', 'spinner']" spin /> Memproses...
            </span>
            <span v-else>
              Lanjutkan Pembayaran
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';
import LanggananBox from '@/components/LanggananBox.vue';
import LoginForm from '@/components/LoginForm.vue';
import RegisterForm from '@/components/RegisterForm.vue';
import axios from 'axios';

export default {
  name: 'Langganan',
  components: {
    LanggananBox,
    LoginForm,
    RegisterForm
  },
  setup() {
    useHead({
      title: 'Langganan - HobiBaca',
      meta: [
        {
          name: 'description',
          content: 'Berlangganan HobiBaca untuk akses ke ribuan buku digital berkualitas'
        },
        {
          property: 'og:title',
          content: 'Langganan - HobiBaca'
        },
        {
          property: 'og:description',
          content: 'Berlangganan HobiBaca untuk akses ke ribuan buku digital berkualitas'
        }
      ]
    });
  },
  data() {
    return {
      showLoginModal: false,
      showPaymentModal: false,
      isLoginMode: true,
      isLoggedIn: !!localStorage.getItem('token'),
      features: [
        "Akses ke SEMUA kategori buku",
        "Update buku baru setiap minggu",
        "Baca offline",
        "Fitur bookmark premium",
        "Prioritas layanan pelanggan",
        "Fitur highlight & catatan",
        "Akses ke konten eksklusif"
      ],
      paymentMethod: '',
      selectedBank: '',
      selectedWallet: '',
      isProcessingPayment: false,
      paymentErrorMessage: ''
    };
  },
  computed: {
    isPaymentMethodSelected() {
      if (this.paymentMethod === 'bank_transfer') {
        return !!this.selectedBank;
      } else if (this.paymentMethod === 'e_wallet') {
        return !!this.selectedWallet;
      }
      return !!this.paymentMethod;
    }
  },
  methods: {
    selectPaymentMethod(method) {
      this.paymentMethod = method;
      // Reset child selections when changing payment method
      if (method !== 'bank_transfer') {
        this.selectedBank = '';
      }
      if (method !== 'e_wallet') {
        this.selectedWallet = '';
      }
    },
    
    async processMidtransPayment() {
      if (!this.isPaymentMethodSelected) return;
      this.isProcessingPayment = true;
      this.paymentErrorMessage = '';
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.showLoginModal = true;
          this.showPaymentModal = false;
          return;
        }
        
        // Prepare payment data based on selected method
        let paymentData = {
          plan_id: 1,
          payment_gateway: 'midtrans'
        };
        
        // Add specific payment method details
        if (this.paymentMethod === 'bank_transfer' && this.selectedBank) {
          paymentData.payment_method = 'bank_transfer';
          paymentData.bank = this.selectedBank;
        } else if (this.paymentMethod === 'e_wallet' && this.selectedWallet) {
          paymentData.payment_method = this.selectedWallet;
        } else if (this.paymentMethod === 'credit_card') {
          paymentData.payment_method = 'credit_card';
        }
        
        console.log('Sending payment data:', paymentData); // Debug log
        
        // Langsung menggunakan endpoint create/ untuk pembuatan subscription
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/subscriptions/create/`,
          paymentData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('Create subscription response:', response.data); // Debug log
        
        if (response.data && response.data.payment_url) {
          // Redirect ke halaman pembayaran Midtrans
          window.location.href = response.data.payment_url;
        } else if (response.data && response.data.token) {
          // Jika dapat token, load Midtrans Snap
          await this.loadMidtransScript();
          
          // Open Midtrans popup dengan token
          window.snap.pay(response.data.token, {
            onSuccess: (result) => {
              this.handlePaymentSuccess(result);
            },
            onPending: (result) => {
              this.handlePaymentPending(result);
            },
            onError: (error) => {
              this.handlePaymentError(error);
            },
            onClose: () => {
              this.isProcessingPayment = false;
            }
          });
        } else {
          throw new Error('Tidak ada URL pembayaran atau token dalam respons');
        }
      } catch (error) {
        console.error('Error processing payment:', error);
        this.isProcessingPayment = false;
        this.paymentErrorMessage = error.response?.data?.message || 'Terjadi kesalahan saat memproses pembayaran';
      }
    },
    
    loadMidtransScript() {
      return new Promise((resolve, reject) => {
        if (window.snap) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = process.env.VUE_APP_MIDTRANS_IS_PRODUCTION
          ? 'https://app.midtrans.com/snap/snap.js'
          : 'https://app.sandbox.midtrans.com/snap/snap.js';
        script.setAttribute('data-client-key', process.env.VUE_APP_MIDTRANS_CLIENT_KEY);
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    },
    
    handleSubscriptionError(error) {
      if (error.status === 401) {
        this.showLoginModal = true;
        this.isLoginMode = true; // Selalu mulai dengan form login
        this.showPaymentModal = false;
      }
    },
    
    handleLoginSuccess() {
      this.showLoginModal = false;
      this.isLoggedIn = true;
      // Setelah login berhasil, tampilkan modal pembayaran
      this.showPaymentModal = true;
    },
    
    handleRegisterSuccess(data) {
      // Simpan token setelah registrasi berhasil
      if (data && data.token) {
        localStorage.setItem('token', data.token);
        // Setelah register berhasil, langsung login
        this.showLoginModal = false;
        this.isLoggedIn = true;
        // Tampilkan modal pembayaran
        this.showPaymentModal = true;
      }
    },
    
    closeLoginModal() {
      this.showLoginModal = false;
      this.isLoginMode = true; // Reset ke mode login saat modal ditutup
    },
    
    closePaymentModal() {
      this.showPaymentModal = false;
      this.paymentMethod = '';
      this.selectedBank = '';
      this.selectedWallet = '';
      this.isProcessingPayment = false;
      this.paymentErrorMessage = '';
    },
    
    toggleAuthMode() {
      this.isLoginMode = !this.isLoginMode;
    },
    
    handlePaymentSuccess(result) {
      this.isProcessingPayment = false;
      this.showPaymentModal = false;
      this.$router.push({
        path: '/payment/success',
        query: { order_id: result.order_id }
      });
    },
    
    handlePaymentPending(result) {
      this.isProcessingPayment = false;
      this.showPaymentModal = false;
      this.$router.push({
        path: '/payment/pending',
        query: { 
          order_id: result.order_id,
          payment_type: result.payment_type,
          gross_amount: result.gross_amount
        }
      });
    },
    
    handlePaymentError(error) {
      this.isProcessingPayment = false;
      this.showPaymentModal = false;
      this.$router.push({
        path: '/payment/error',
        query: {
          message: error.message || 'Terjadi kesalahan saat memproses pembayaran',
          order_id: error.order_id || null,
          payment_type: error.payment_type || null
        }
      });
    }
  }
};
</script>

<style scoped>
.langganan-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
}

.header {
  text-align: center;
  margin-bottom: 3rem;
}

.header h1 {
  font-size: 2.5rem;
  color: #1a1a1a;
  margin-bottom: 1rem;
}

.header p {
  font-size: 1.1rem;
  color: #666;
}

.single-subscription {
  max-width: 500px;
  margin: 2rem auto;
}

/* Payment Modal Styling */
.payment-modal {
  max-width: 550px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1f2937;
}

.modal-body {
  padding: 0.5rem 0;
}

.payment-error-message {
  background-color: #fdecea;
  color: #b71c1c;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-icon {
  font-size: 1.2rem;
}

/* Payment Section Styling */
.payment-methods {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.payment-method-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.payment-method-card:hover {
  border-color: #488e71;
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(72, 142, 113, 0.15);
}

.payment-method-card.selected {
  border-color: #488e71;
  background-color: #f0f7f4;
}

.payment-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  margin-right: 12px;
}

.method-details {
  flex: 1;
}

.method-name {
  display: block;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.method-desc {
  display: block;
  font-size: 0.8rem;
  color: #6b7280;
}

.check-indicator {
  color: #488e71;
  font-size: 1.2rem;
}

/* Bank and E-Wallet Selection */
.bank-selection,
.wallet-selection {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.bank-selection h4,
.wallet-selection h4 {
  font-size: 1rem;
  color: #1f2937;
  margin-bottom: 1rem;
}

.bank-options,
.wallet-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 1rem;
}

.bank-option,
.wallet-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.bank-option:hover,
.wallet-option:hover {
  border-color: #488e71;
  transform: translateY(-2px);
}

.bank-option.selected,
.wallet-option.selected {
  border-color: #488e71;
  background-color: #f0f7f4;
}

.bank-logo,
.wallet-logo {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 8px;
}

.bank-option span,
.wallet-option span {
  font-size: 0.9rem;
  font-weight: 500;
  color: #1f2937;
}

.proceed-payment-btn {
  width: 100%;
  padding: 1rem;
  background-color: #488e71;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.proceed-payment-btn:hover:not(:disabled) {
  background-color: #3a7259;
  transform: translateY(-2px);
}

.proceed-payment-btn:disabled {
  background-color: #93c3b1;
  cursor: not-allowed;
  transform: none;
}

/* Auth Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
}

.close-button:hover {
  color: #1f2937;
}

.auth-divider {
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 45%;
  height: 1px;
  background-color: #e5e7eb;
}

.auth-divider::before {
  left: 0;
}

.auth-divider::after {
  right: 0;
}

.auth-divider span {
  background-color: white;
  padding: 0 1rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.auth-switch {
  text-align: center;
  margin-top: 1rem;
  color: #4b5563;
  font-size: 0.95rem;
}

.auth-switch a {
  color: #488e71;
  text-decoration: none;
  font-weight: 500;
  margin-left: 0.5rem;
}

.auth-switch a:hover {
  color: #3a7259;
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .langganan-container {
    padding: 1rem;
  }

  .header h1 {
    font-size: 2rem;
  }

  .payment-method-card {
    padding: 0.75rem;
  }

  .bank-options,
  .wallet-options {
    grid-template-columns: repeat(2, 1fr);
  }

  .modal-content {
    width: 90%;
    padding: 1.5rem;
  }
}
</style>